import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, FormGroup } from '@mui/material';
import { AppDispatch, RootState } from '../../../redux/store';
import { addComment } from "../../../redux/actions/comments.actions";
import { useParams, useSearchParams } from "react-router-dom";
import { CategoryLabels } from "../categoryLabels.enum";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import MDEditor from '@uiw/react-md-editor';
import useRoleCheck from "../../../hooks/useRoleCheck";
import { localizePal } from "../../../utils/brand";
import ModalShell from "../../../components/common/Modals/ModalShell";

const AddCommentModal: React.FC<ModalProps> = ({open, setOpen}) => {
  const {id} = useParams();
  const searchParams = useSearchParams()[0];

  const dispatch = useDispatch<AppDispatch>();
  const { data:user, accessToken } = useSelector((state: RootState) => state.user);
  const { error, loading } = useSelector((state: RootState) => state.comments);
  const { canAccess: isDeveloper } = useRoleCheck(['developer']);

  const [submitted, setSubmitted] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('');
  const [devOnly, setDevOnly] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if(id) {
      await dispatch(addComment({
        title, category, body, spa_id: id, user_id: user?.id,
        params: searchParams.toString(),
        ...(isDeveloper && { dev_only: devOnly }),
      }));
    }
  };

  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  const handleReset = () => {
    setSubmitted(false);
    setTitle('');
    setBody('');
    setCategory('');
    setDevOnly(false);
  };

  const editing = !submitted || loading;
  const colorMode = localStorage.getItem('data-theme') ?? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={editing ? "lg" : "sm"}
      busy={submitted && loading}
      tone={editing ? undefined : error ? "error" : "success"}
      title={editing ? (submitted && loading ? "Posting comment…" : "New comment") : error ? "An error occurred" : "Comment added"}
      actions={editing ?
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !accessToken || title===''||body===''||category===''}
          >
            {loading ? "Posting…" : "Post"}
          </button>
        </>
        : error ?
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          <button className="btn btn-primary" onClick={handleReset}>Try again</button>
        </>
        :
        <>
          <button className="btn dlg-actions-start" onClick={handleReset}>Add another</button>
          <button className="btn btn-primary" onClick={handleClose}>Done</button>
        </>
      }
    >
      {editing ?
        <form className="dlg-section" onSubmit={handleSubmit}>
          <div className="field">
            <label className="field-label" htmlFor="title">Title</label>
            <input
              id="title"
              className="input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={loading}
              autoComplete="off"
            />
          </div>
          <FormControl fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={e => setCategory(e.target.value as string)}
              defaultValue=""
            >
              {Object.keys(CategoryLabels).map((cat:any, i:number)=>
                <MenuItem key={i} value={cat}>{localizePal(CategoryLabels[cat as keyof typeof CategoryLabels])}</MenuItem>
              )}
            </Select>
          </FormControl>

          <div className="field" data-color-mode={colorMode}>
            <span className="field-label">Body</span>
            <MDEditor value={body} onChange={(value) => setBody(value??"")} aria-required={true}/>
          </div>
          {isDeveloper && (
            <div>
              <FormGroup>
                <FormControlLabel
                  label="Dev only"
                  control={
                    <Checkbox
                      checked={devOnly}
                      onChange={(e) => setDevOnly(e.target.checked)}
                      disabled={loading}
                    />
                  }
                />
              </FormGroup>
              {devOnly && (
                <p className="dlg-muted">Dev only comments are visible only to users with the developer role.</p>
              )}
            </div>
          )}
        </form>
        : error ?
        <p>{error}</p>
        :
        <p>Comment successfully added.</p>
      }
    </ModalShell>
  );
};

export default AddCommentModal;
